import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { FolderKanban, Plus } from "lucide-react";
import { pm, errorMessage } from "../pm/api";
import { useHd } from "./HelpdeskContext";
import { SmallLabel } from "./ui";
import { GlassCard, T, TYPE, primaryButtonSx } from "../user/complaints/shared";

// Ticket sidebar card: project-management issues raised from / linked to this
// ticket, plus "Create issue". Hidden for agents without PM access.

const CATEGORY_COLORS = { done: ["#ECFDF3", "#067647"], in_progress: ["#EFF8FF", "#175CD3"], todo: ["#F2F4F7", "#344054"] };

export default function LinkedIssues({ ticket }) {
  const ctx = useHd();
  const [rows, setRows] = useState(null);
  const [noAccess, setNoAccess] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    pm.ticketIssues(ticket._id).then(setRows).catch((err) => {
      if ([401, 403].includes(err?.response?.status)) setNoAccess(true);
      else setRows([]);
    });
  }, [ticket._id]);
  useEffect(() => {
    load();
  }, [load]);
  if (noAccess) return null;
  return (
    <GlassCard sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <SmallLabel sx={{ flexGrow: 1 }}>Linked issues</SmallLabel>
        <Button size="small" startIcon={<Plus size={14} />} onClick={() => setOpen(true)} sx={{ textTransform: "none", fontWeight: 600 }}>Create issue</Button>
      </Stack>
      {!rows ? <CircularProgress size={18} /> : !rows.length ? (
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Raise a bug or task for the product team from this ticket.</Typography>
      ) : (
        <Stack spacing={1}>
          {rows.map((i) => {
            const [bg, fg] = CATEGORY_COLORS[i.statusCategory] || CATEGORY_COLORS.todo;
            return (
              <Box key={i._id}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FolderKanban size={14} color={T.inkMuted} />
                  <Link component={RouterLink} to={`/projects/${i.project?.key}/issues/${i.key}`} sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{i.key}</Link>
                  <Chip size="small" label={i.status || i.statusCategory} sx={{ height: 18, fontSize: "0.65rem", bgcolor: bg, color: fg, fontWeight: 700 }} />
                </Stack>
                <Typography noWrap sx={{ fontSize: TYPE.micro, color: T.inkSecondary, pl: 2.75 }}>{i.summary}{i.assignee ? ` · ${i.assignee}` : ""}</Typography>
              </Box>
            );
          })}
        </Stack>
      )}
      {open && <CreateIssueDialog ticket={ticket} onClose={() => setOpen(false)} onCreated={(m) => { setOpen(false); ctx.notify(m); load(); }} />}
    </GlassCard>
  );
}

function CreateIssueDialog({ ticket, onClose, onCreated }) {
  const [projects, setProjects] = useState(null);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ projectId: "", typeId: "", summary: `[${ticket.key}] ${ticket.subject}`.slice(0, 500), note: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([pm.projects(), pm.meta()]).then(([p, m]) => {
      const active = (p || []).filter((x) => !x.archivedAt);
      setProjects(active);
      setMeta(m);
      if (active.length === 1) setForm((f) => ({ ...f, projectId: active[0]._id }));
    }).catch((err) => setError(errorMessage(err, "Could not load projects")));
  }, []);
  const project = projects?.find((p) => p._id === form.projectId);
  const types = useMemo(() => {
    if (!project || !meta) return [];
    const allowed = new Set((project.issueTypeIds || []).map(String));
    return meta.issueTypes.filter((t) => allowed.has(String(t._id)) && t.isActive !== false && t.level === 1);
  }, [project, meta]);
  useEffect(() => {
    if (types.length && !types.find((t) => t._id === form.typeId)) setForm((f) => ({ ...f, typeId: (types.find((t) => t.key === "bug") || types[0])._id }));
  }, [types]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const create = async () => {
    setBusy(true);
    setError("");
    try {
      const issue = await pm.createIssueFromTicket(form.projectId, { ticketId: ticket._id, typeId: form.typeId || undefined, summary: form.summary, note: form.note });
      onCreated(`${issue.key} created in ${project?.key}`);
    } catch (err) {
      setError(errorMessage(err, "Could not create the issue"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create issue from {ticket.key}</DialogTitle>
      <DialogContent dividers>
        {!projects ? (error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={22} />) : !projects.length ? (
          <Alert severity="info">You are not a member of any active project yet.</Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField select fullWidth size="small" label="Project" value={form.projectId} onChange={set("projectId")}>
                {projects.map((p) => <MenuItem key={p._id} value={p._id}>{p.key} — {p.name}</MenuItem>)}
              </TextField>
              <TextField select fullWidth size="small" label="Type" value={form.typeId} onChange={set("typeId")} disabled={!types.length}>
                {types.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField fullWidth size="small" label="Summary" value={form.summary} onChange={set("summary")} inputProps={{ maxLength: 500 }} />
            <TextField fullWidth multiline minRows={3} size="small" label="Note for the team (optional)" value={form.note} onChange={set("note")} inputProps={{ maxLength: 4000 }}
              helperText="The ticket's description and a link back to it are added automatically." />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={busy || !form.projectId || !form.summary.trim()} onClick={create}>{busy ? "Creating…" : "Create issue"}</Button>
      </DialogActions>
    </Dialog>
  );
}
